import React, { useContext } from 'react';
import { css, StyledComponent } from 'styled-components';
import { screenMdMax } from '@spotify-internal/encore-foundation';

const DURATION = '500ms';

export const DELAYS = {
  FIRST: 0,
  SECOND: 250,
  THIRD: 500,
  FOURTH: 750,
  FIFTH: 1000,
  SIXTH: 1250,
  SEVENTH: 1500,
  EIGHTH: 1750,
  NINTH: 2000,
  TENTH: 2250,
};

export const Crossfade = css<{ active: boolean }>`
  /* cross-fade */
  opacity: ${({ active }) => (active ? 1 : 0)};
  z-index: ${({ active }) => (active ? 1 : 'unset')};
  transition: opacity 0.75s linear, z-index 0s 0.75s, color 0s 0.75s;
`;

interface IDictionary<TValue> {
  [key: string]: TValue;
}

const customThresholds: IDictionary<number> = {
  featuredContent2Up: 0.4,
  featuredContent3Up: 0.4,
  groupTestimonial: 0.6,
  editorialGrid: 0.6,
  relatedLinksModule: 0.5,
  singleTestimonial: 0.6,
};

export const customThreshold = (contentType: string) => {
  if (contentType in customThresholds) {
    return customThresholds[contentType];
  }
  return 0.25;
};

const applyKeyframes = css`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const applyStartState = (props: { animationContext: AnimationContextType }) =>
  !props.animationContext?.isOnScreen &&
  props.animationContext?.moduleAnimatable &&
  props.animationContext?.hasAnimated < 1
    ? `opacity: 0;`
    : '';

const applyAnimation = (
  delay: number,
  props: { animationContext?: AnimationContextType },
) =>
  props.animationContext?.isOnScreen &&
  props.animationContext?.moduleAnimatable &&
  props.animationContext?.hasAnimated < 2
    ? `animation: ${DURATION} linear ${delay}ms 1 both fadeIn;`
    : '';

// get animation text for staggered text animations in modules
// hook for most performant way i could find to find out if an element is on screen
function useOnScreen(ref: any, rootMargin = '0px', threshold = 0.25) {
  const [isIntersecting, setIntersecting] = React.useState(false);

  React.useEffect(() => {
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIntersecting(entry.isIntersecting);
        },
        {
          rootMargin,
          threshold,
        },
      );
      if (ref?.current) {
        observer.observe(ref.current);
      }
      return () => {
        if (ref?.current) {
          observer.unobserve(ref.current);
        }
      };
    }
    return () => {};
  }, []);
  return isIntersecting;
}

export type AnimationContextType = {
  isOnScreen: boolean;
  moduleAnimatable: boolean;
  hasAnimated: number;
};

export const AnimationContext = React.createContext<AnimationContextType>({
  isOnScreen: false,
  moduleAnimatable: false,
  hasAnimated: 0,
});

export function useAnimations(
  contentType: string,
  shouldAnimate: boolean,
  threshold: number,
) {
  const animationRef = React.createRef();
  const [hasAnimated, setHasAnimated] = React.useState(0);
  const isOnScreen = useOnScreen(animationRef, '0px', threshold);
  const moduleAnimatable = shouldAnimate && isAnimatable(contentType);

  React.useEffect(() => {
    if (isOnScreen) {
      setHasAnimated(hasAnimated + 1);
    }
  }, [isOnScreen]);

  const animationContext: AnimationContextType = {
    isOnScreen,
    moduleAnimatable,
    hasAnimated,
  };
  return { animationContext, animationRef };
}

export function fadeInAnimation(delay: number) {
  return css<{
    animationContext: AnimationContextType;
  }>`
    @media (min-width: ${screenMdMax}) {
      ${applyKeyframes};
      ${props => applyStartState(props)};
      ${props => applyAnimation(delay, props)};
    }
  `;
}

export function withAnimationContext(
  Component: React.ComponentType<any> | StyledComponent<any, any>,
) {
  return (props: any) => {
    const animationContext = useContext(AnimationContext);
    return <Component {...props} animationContext={animationContext} />;
  };
}

const isAnimatable = (mod: string) =>
  [
    'editorialGridModule',
    'heroBodyModule',
    'headerModule',
    'heroBodyCarousel',
    'heroHeaderCarousel',
    'heroNarrativeCarousel',
    'faqLinksModule',
    'groupTestimonial',
    'heroCaseStudyCover',
    'singleTestimonial',
    'singleFeatureModule',
    'pageBreakerModule',
    'featuredContent2Up',
    'featuredContent3up',
    'featuredContentModule',
    'relatedLinksModule',
  ].includes(mod);
